import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { DirectionalHint, DropdownMenuItemType, IDropdown } from '@fluentui/react'
import BaseDropdown, { DropdownOption, DropdownProps } from './BaseDropdown'
import { FilePartyDto, FilePartyRole, MainDto } from '../../config/filePartyConfig'
import useViewport from '../../hooks/useViewport'
import { FilePartyRoleDto } from '../../context/GlobalContext'
import { useSettings } from '../../hooks/useSettings'

export interface IFilePartyDropdownOption<D = {}> extends DropdownOption {
    role?: FilePartyRole | string // Required for grouping.
    data?: D
}

type Props = DropdownProps & {
    options: IFilePartyDropdownOption[]
    onRenderName?: (option: IFilePartyDropdownOption) => React.ReactNode
    focusOnMount?: boolean
}

const mapOptions = (options: IFilePartyDropdownOption[], roles: FilePartyRoleDto[]): (IFilePartyDropdownOption | DropdownOption)[] => {
    // Extract all unique roles from the file party options by creating a set.
    const activeRoles: (FilePartyRole | string)[] = [...new Set((options || []).map(option => option.role))]
    let i = 0

    return (roles || [])
        .filter((role: FilePartyRoleDto) => activeRoles.some(activeRole => activeRole.toString() === role.type))
        .flatMap((role: FilePartyRoleDto) => [
            // Only add a divider if this is not the first role group.
            ...(role !== roles?.[0]
                ? [
                      {
                          key: `file-party-dropdown-divider-${i++}`,
                          text: '-',
                          itemType: DropdownMenuItemType.Divider,
                      },
                  ]
                : []),
            // Set a header with the current group.
            {
                key: role.type,
                text: role.label,
                itemType: DropdownMenuItemType.Header,
            },
            // Add all the file parties that contain the current role.
            ...(options || [])
                .filter((fileParty: IFilePartyDropdownOption) => fileParty.role === role.type)
                .flatMap((fileParty: IFilePartyDropdownOption) => [
                    {
                        ...fileParty,
                        itemType: DropdownMenuItemType.Normal,
                    },
                ]),
        ])
}

export function mapPartyDtosToDropdownOptions(parties: (MainDto | FilePartyDto)[]): IFilePartyDropdownOption<MainDto | FilePartyDto>[] {
    return parties.flatMap((party: MainDto | FilePartyDto) => [
        {
            key: party.pivotId,
            text: party.displayName,
            role: party.role,
            // Do not hide items that have no e-mail address, because they could have children with an e-mail.
            disabled: !party.email,
            data: party,
        },
        ...(party.hasOwnProperty('children') ? mapPartyDtosToDropdownOptions((party as MainDto).children) : []),
    ])
}

export default function LegacyFilePartyDropdown(props: Props): ReactElement {
    const [options, setOptions] = useState<(IFilePartyDropdownOption | DropdownOption)[]>([])
    const { settings } = useSettings()
    const { height } = useViewport()
    const dropdownRef = useRef<IDropdown>()

    useEffect(() => {
        if (settings?.filePartyRoles) {
            setOptions(mapOptions(props.options, settings?.filePartyRoles))
        }
    }, [settings, props.options])

    useEffect(() => {
        if (props.focusOnMount) {
            dropdownRef.current?.focus(true)
        }
    }, [dropdownRef.current])

    return (
        <BaseDropdown
            componentRef={dropdownRef}
            {...props}
            calloutProps={{
                directionalHint: DirectionalHint.bottomAutoEdge,
                directionalHintFixed: false,
                calloutMaxHeight: height / 2 - 32,
                ...(props.calloutProps || {}),
            }}
            options={options}
            onRenderOption={(option: IFilePartyDropdownOption) => (
                <>{option.itemType === DropdownMenuItemType.Normal && props.onRenderName ? props.onRenderName(option) : option.text}</>
            )}
        />
    )
}
